// src/components/Form1.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import "../css/coaching-form.css";
import { API_BASE_URL } from "../config";

/**
 * Reusable CoachingForm
 */

const DEFAULT_PACKAGES = {
  weekly: { key: "weekly", name: "Weekly", price: 39.99, period: "week" },

  monthly: {
    key: "monthly",
    name: "Monthly",
    price: 139.99,
    period: "month",
  },

  threem: {
    key: "threem",
    name: "3-Month",
    price: 249.99,
    period: "3 months",
  },

  sixm: {
    key: "sixm",
    name: "6-Month",
    price: 599.99,
    period: "6 months",
  },
};

export default function Form1({
  onSubmit,
  showHero = false,
  eyebrow = "Ready to get coached?",
  headline = "Fill the form and request a call",
  copy = "Tell me about your goals and schedule. I’ll get back to you to plan a free 15’ discovery call.",
  id = "consultation",
  defaultPackage,
  packagesData,
  currency = "EUR",
  leadSource = "coaching-form",
}) {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth(); // <-- prendiamo anche user

  const [submitting, setSubmitting] = useState(false);

  // ========== PACKAGES ==========
  const PACKS = useMemo(() => {
    if (!packagesData) return DEFAULT_PACKAGES;
    const normalize = (p) => ({
      key: p.key,
      name: p.name,
      price: Number(p.price),
      period: p.period,
      compare: p.compare != null ? Number(p.compare) : undefined,
      save:
        p.save != null
          ? Number(p.save)
          : p.compare
          ? Number(p.compare) - Number(p.price)
          : undefined,
    });
    return Object.fromEntries(
      Object.entries(packagesData).map(([k, v]) => [k, normalize(v)])
    );
  }, [packagesData]);

  const initialFromQuery = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get("package");
      if (!q) return null;
      const key = q.toLowerCase();
      return PACKS[key] ? key : null;
    } catch {
      return null;
    }
  }, [PACKS]);

  const [selectedKey, setSelectedKey] = useState(
    initialFromQuery || defaultPackage || ""
  );
  const [showPackagePicker, setShowPackagePicker] = useState(!selectedKey);

  useEffect(() => {
    if (!selectedKey && (initialFromQuery || defaultPackage)) {
      setSelectedKey(initialFromQuery || defaultPackage || "");
      setShowPackagePicker(!(initialFromQuery || defaultPackage));
    }
  }, [initialFromQuery, defaultPackage, selectedKey]);

  const selectedPack = selectedKey ? PACKS[selectedKey] : null;

  // ========== TIMELINE ==========
  const [timeline, setTimeline] = useState("");

  const baseWeeks = useMemo(
    () =>
      Array.from({ length: 8 }, (_, i) => {
        const n = i + 1;
        const lbl = `${n} week${n > 1 ? "s" : ""}`;
        return { value: lbl, label: lbl };
      }),
    []
  );

  const baseMonths = useMemo(
    () =>
      Array.from({ length: 6 }, (_, i) => {
        const n = i + 1;
        const lbl = `${n} month${n > 1 ? "s" : ""}`;
        return { value: lbl, label: lbl };
      }),
    []
  );

  const TIMELINE_OPTIONS = useMemo(() => {
    const ongoing = {
      value: "Ongoing / not sure",
      label: "Ongoing / not sure",
    };
    return [...baseWeeks, ...baseMonths, ongoing];
  }, [baseWeeks, baseMonths]);

  useEffect(() => {
    const values = TIMELINE_OPTIONS.map((o) => o.value);
    if (!values.includes(timeline)) setTimeline(values[0] || "");
  }, [TIMELINE_OPTIONS, timeline]);

  // ==========================================
  // =========== SUBMIT HANDLER ===============
  // ==========================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedPack) {
      alert("Please select a coaching package to continue.");
      return;
    }

    // 🔐 Se non loggato → blocca e manda al login
    if (!isAuthenticated || !user?.id) {
      alert(
        "You must be registered to request coaching.\nPlease sign in or create an account."
      );
      navigate("/login");
      return;
    }

    if (submitting) return;
    setSubmitting(true);

    const formEl = e.currentTarget;
    const raw = Object.fromEntries(new FormData(formEl));

    const payload = {
      ...raw,
      timeline,
      package_type: selectedPack.key,
      package_name: selectedPack.name,
      package_price: selectedPack.price,
      package_period: selectedPack.period,
      package_compare: selectedPack.compare ?? "",
      package_saving: selectedPack.save ?? "",
      currency,
      lead_source: leadSource,
    };

    try {
      // 1️⃣ Contact → email all'admin
      const resContact = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const text = await resContact.text();
      if (!resContact.ok) throw new Error(text || `HTTP ${resContact.status}`);

      // 2️⃣ CoachingSubscription → salva in DB come "pending"
      const coachingPayload = {
        userId: user.id,
        packageId: selectedPack.key,
        packageTitle: selectedPack.name,
        timeline,

        // 🔽 campi form (da raw)
        name: raw.name,
        email: raw.email,
        phone: raw.phone,
        message: raw.message,
        sex: raw.sex,
        age: Number(raw.age),
        height: Number(raw.height),
        weight: Number(raw.weight),
        experience: raw.experience,
        objective: raw.objective,
        frequency: raw.frequency,
        coaching: raw.coaching,
        timezone: raw.timezone,
        calltimes: raw.calltimes,
        lead_source: leadSource,
      };

      const resCoaching = await fetch(`${API_BASE_URL}/api/coaching`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(coachingPayload),
      });

      const coachingText = await resCoaching.text();
      if (!resCoaching.ok) {
        throw new Error(coachingText || `Coaching HTTP ${resCoaching.status}`);
      }

      if (onSubmit) onSubmit(payload);

      alert(
        text ||
          "Thanks! I’ll get back to you shortly to plan your discovery call."
      );

      // Reset form
      formEl.reset();
      const nextValues = TIMELINE_OPTIONS.map((o) => o.value);
      setTimeline(nextValues[0] || "");
    } catch (err) {
      console.error("Submit error:", err);
      alert(
        "There was a problem sending your request. Please try again later."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatMoney = (value) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return `${currency} ${value}`;
    }
  };

  // ==========================================
  // =============== RENDER ===================
  // ==========================================

  return (
    <>
      <div className="formcontainer">
        {showHero && (
          <section className="consult-hero py-5">
            <Container>
              <Row className="justify-content-center">
                <Col lg={10} xl={8}>
                  <h2 className="consult-hero__title">
                    <span className="consult-hero__eyebrow">{eyebrow}</span>
                    <span className="consult-hero__headline">{headline}</span>
                  </h2>
                  <p className="consult-hero__copy">{copy}</p>
                </Col>
              </Row>
            </Container>
          </section>
        )}

        <section id={id} className="consult-form py-5">
          <Container>
            <Row className="justify-content-center">
              <Col lg={8} xl={7}>
                {/* ================== PACKAGE PICKER ================== */}
                <div className="cf-package">
                  {selectedPack && !showPackagePicker ? (
                    <div className="cf-package-summary">
                      <div className="cf-chip">
                        <span className="cf-chip__title">
                          {selectedPack.name}
                        </span>
                        <span className="cf-chip__price">
                          {formatMoney(selectedPack.price)}{" "}
                          <span className="cf-chip__period">
                            / {selectedPack.period}
                          </span>
                        </span>
                      </div>

                      <div className="cf-package-right">
                        {selectedPack.save ? (
                          <span className="cf-badge-save">
                            Save {formatMoney(selectedPack.save)}
                          </span>
                        ) : null}
                        {selectedPack.compare ? (
                          <span className="cf-badge-strike">
                            anziché {formatMoney(selectedPack.compare)}
                          </span>
                        ) : null}
                        <button
                          type="button"
                          className="cf-link"
                          onClick={() => setShowPackagePicker(true)}
                          aria-label="Change package"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="cf-package-picker">
                      <label htmlFor="package" className="text-light">
                        Select a coaching package and let's start *
                      </label>
                      <div className="cf-select-wrap">
                        <select
                          id="package"
                          name="package"
                          required
                          value={selectedKey}
                          onChange={(e) => setSelectedKey(e.target.value)}
                        >
                          <option value="" disabled>
                            Choose package
                          </option>
                          {Object.values(PACKS).map((p) => (
                            <option key={p.key} value={p.key}>
                              {p.name} — {formatMoney(p.price)} / {p.period}
                              {p.save ? ` (Save ${formatMoney(p.save)})` : ""}
                            </option>
                          ))}
                        </select>

                        <button
                          type="button"
                          className="cf-btn-ghost"
                          onClick={() =>
                            selectedKey && setShowPackagePicker(false)
                          }
                          disabled={!selectedKey}
                        >
                          Confirm
                        </button>
                      </div>

                      {selectedKey && PACKS[selectedKey]?.compare ? (
                        <p className="cf-picker-note text-light">
                          You save {formatMoney(PACKS[selectedKey].save)} —
                          instead {formatMoney(PACKS[selectedKey].compare)}
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>

                {/* ================== FORM ================== */}
                <form className="cf-form" onSubmit={handleSubmit}>
                  {/* honeypot */}
                  <input
                    type="text"
                    name="_hp"
                    className="hp-field"
                    tabIndex="-1"
                    autoComplete="off"
                  />

                  {/* identity */}
                  <div className="cf-row">
                    <div className="cf-field">
                      <label htmlFor="name">Full name *</label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="cf-field">
                      <label htmlFor="email">Email *</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>

                  {/* demographics */}
                  <div className="cf-row cf3">
                    <div className="cf-field">
                      <label htmlFor="sex">Sex *</label>
                      <select id="sex" name="sex" required defaultValue="">
                        <option value="" disabled>
                          Select
                        </option>
                        <option>Male</option>
                        <option>Female</option>
                        <option>Non-binary</option>
                        <option>Prefer not to say</option>
                      </select>
                    </div>
                    <div className="cf-field">
                      <label htmlFor="age">Age *</label>
                      <input
                        id="age"
                        name="age"
                        type="number"
                        min="10"
                        max="100"
                        required
                        placeholder="28"
                      />
                    </div>
                    <div className="cf-field">
                      <label htmlFor="phone">Phone (incl. country code)</label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="+39 345 123 4567"
                      />
                    </div>
                  </div>

                  {/* measures */}
                  <div className="cf-row cf3">
                    <div className="cf-field">
                      <label htmlFor="height">Height (cm) *</label>
                      <input
                        id="height"
                        name="height"
                        type="number"
                        min="100"
                        max="250"
                        required
                        placeholder="178"
                      />
                    </div>
                    <div className="cf-field">
                      <label htmlFor="weight">Weight (kg) *</label>
                      <input
                        id="weight"
                        name="weight"
                        type="number"
                        min="30"
                        max="250"
                        required
                        placeholder="78"
                      />
                    </div>
                    <div className="cf-field">
                      <label htmlFor="experience">Training experience</label>
                      <select id="experience" name="experience" defaultValue="">
                        <option value="">Select one (optional)</option>
                        <option>Beginner (0–6 months)</option>
                        <option>Intermediate (6–24 months)</option>
                        <option>Advanced (2+ years)</option>
                      </select>
                    </div>
                  </div>

                  {/* goals & preferences */}
                  <div className="cf-row">
                    <div className="cf-field">
                      <label htmlFor="objective">Objective *</label>
                      <select
                        id="objective"
                        name="objective"
                        required
                        defaultValue=""
                      >
                        <option value="" disabled>
                          Select your objective
                        </option>
                        <option value="fat-loss">Fat loss</option>
                        <option value="hypertrophy">Hypertrophy</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="strength">Strength</option>
                        <option value="mobility">Mobility</option>
                      </select>
                    </div>
                    <div className="cf-field">
                      <label htmlFor="frequency">
                        Weekly training frequency *
                      </label>
                      <select
                        id="frequency"
                        name="frequency"
                        required
                        defaultValue=""
                      >
                        <option value="" disabled>
                          Select frequency
                        </option>
                        <option>2 days/week</option>
                        <option>3 days/week</option>
                        <option>4 days/week</option>
                        <option>5+ days/week</option>
                      </select>
                    </div>
                  </div>

                  {/* coaching + timeline + selected package */}
                  <div className="cf-row cf3 cf-row--package">
                    <div className="cf-field cf-coaching">
                      <label htmlFor="coaching">Preferred coaching</label>
                      <select id="coaching" name="coaching" defaultValue="">
                        <option value="">Select (optional)</option>
                        <option>In-person (Rome)</option>
                        <option>Online / Remote</option>
                        <option>Hybrid</option>
                      </select>
                    </div>

                    <div className="cf-field cf-timeline">
                      <label htmlFor="timeline">Desired timeline</label>
                      <select
                        id="timeline"
                        name="timeline"
                        value={timeline}
                        onChange={(e) => setTimeline(e.target.value)}
                      >
                        {TIMELINE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <small className="cf-note">
                        If your desired timeline doesn’t perfectly match our
                        preset packages, we’ll provide a{" "}
                        <strong>custom quote</strong> (especially for timelines
                        over one month) so you can still <strong>save</strong>{" "}
                        compared to the weekly rate.
                      </small>
                    </div>

                    <div className="cf-field cf-selected">
                      <label htmlFor="selected-package-display">
                        Selected package
                      </label>
                      <input
                        id="selected-package-display"
                        type="text"
                        className="cf-readonly"
                        value={
                          selectedPack
                            ? `${selectedPack.name} — ${formatMoney(
                                selectedPack.price
                              )} / ${selectedPack.period}`
                            : "No package selected"
                        }
                        readOnly
                        disabled
                        aria-readonly="true"
                      />
                    </div>
                  </div>

                  {/* logistics */}
                  <div className="cf-row">
                    <div className="cf-field">
                      <label htmlFor="timezone">Time zone</label>
                      <input
                        id="timezone"
                        name="timezone"
                        type="text"
                        placeholder="Europe/Rome or UTC+1"
                      />
                    </div>
                    <div className="cf-field">
                      <label htmlFor="calltimes">Preferred call times</label>
                      <input
                        id="calltimes"
                        name="calltimes"
                        type="text"
                        placeholder="Weekdays 18:00–20:00"
                      />
                    </div>
                  </div>

                  <div className="cf-field">
                    <label htmlFor="message">Anything I should know?</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      placeholder="Injuries, schedule constraints, equipment availability, nutrition notes..."
                    />
                  </div>

                  <div className="cf-actions">
                    <button
                      type="submit"
                      className="cf-btn-primary"
                      disabled={!selectedPack || submitting}
                    >
                      {submitting ? "Sending..." : "Request a call"}
                    </button>
                    <p className="cf-legal">
                      By submitting, you agree to be contacted regarding
                      coaching services.
                    </p>
                  </div>
                </form>
              </Col>
            </Row>
          </Container>
        </section>
      </div>
    </>
  );
}
